import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GAMES } from '../../data/games';
import './Home.css';

const ArrowIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 5v14l12-7z" fill="currentColor" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="home-chk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const DEAL = [
  { title: '100% IP ownership', desc: 'You keep everything you create. We never take your rights.' },
  { title: 'Performance-based split', desc: 'A revenue split that only grows when your game does.' },
  { title: 'Real-time metrics', desc: 'Full, live access to every number behind your launch.' },
  { title: 'Post-launch support', desc: 'We stick around after release, not just for launch week.' },
];

const WHAT_WE_DO = [
  { k: 'Publishing', h: 'Get it on the shelf', p: 'Store listings, launch strategy, and platform relationships.' },
  { k: 'Marketing', h: 'Get it in front of players', p: 'Targeted campaigns, community building, press outreach.' },
  { k: 'Long-term', h: 'Keep it alive', p: 'Post-launch updates, seasonal campaigns, ongoing support.' },
];

export const Home: React.FC = () => {
  useEffect(() => {
    document.title = 'Practical Media - Developer First Publishing';
  }, []);
  return (
    <div className="home">
      <header className="home-hero">
        <video className="home-hero-vid" autoPlay muted loop playsInline poster="/memori_hero_poster.jpg">
          <source src={`${import.meta.env.VITE_API_URL || 'https://api.practicalmedia.com'}/resources/memori_hero_bg`} type="video/mp4" />
        </video>
        <div className="home-hero-scrim" aria-hidden="true" />
        <div className="home-hero-inner container">
          <p className="home-eyebrow">From the founder of <a href="https://solodevelopment.org" target="_blank" rel="noopener noreferrer" className="hero-solodev">SoloDevelopment</a></p>
          <h1 className="home-title">Developer-first <span className="hl">publishing.</span></h1>
          <p className="home-intro">A publisher that respects your rights and celebrates your success. You keep your IP, you keep control, and you get a partner who actually ships games and understands developers.</p>
          <div className="cta-row home-cta">
            <Link to="/contact" className="primary-btn"><PlayIcon />Launch your game</Link>
            <a href="https://discord.com/invite/U7dYpq5yMK" target="_blank" rel="noopener noreferrer nofollow" className="btn-ghost">Chat on Discord</a>
          </div>
          <div className="home-stats">
            <div className="home-stat"><b>100%</b><span>IP stays yours</span></div>
            <div className="home-stat"><b>{GAMES.length}</b><span>games on the roster</span></div>
            <div className="home-stat"><b>Live</b><span>metrics, start to finish</span></div>
          </div>
        </div>
      </header>

      <main>
        <section className="container home-block">
          <div className="home-sec-head">
            <span className="label">The roster</span>
            <Link to="/games" className="home-sec-link">All games <ArrowIcon /></Link>
          </div>
          <div className="home-games">
            {GAMES.map((game) => {
              const primary = game.links.find((l) => l.primary);
              const img = game.coverImage || game.heroImage;
              return (
                <article
                  className="home-game"
                  key={game.slug}
                  style={{ '--ga': game.accent } as React.CSSProperties}
                >
                  <Link to={`/games/${game.slug}`} className="home-game-media" aria-label={game.title}>
                    <img src={img} alt={`${game.title} key art`} loading="lazy" />
                  </Link>
                  <div className="home-game-body">
                    <div className="home-game-top">
                      <h3><Link to={`/games/${game.slug}`}>{game.title}{game.subtitle ? <span className="home-game-sub">{game.subtitle}</span> : null}</Link></h3>
                      <span className="home-status">{game.status}</span>
                    </div>
                    <p className="home-studio">{game.studio}</p>
                    <p className="home-tag">{game.tagline}</p>
                    <div className="home-game-links">
                      <Link to={`/games/${game.slug}`} className="home-play">Details <ArrowIcon /></Link>
                      {primary && (
                        <a href={primary.href} target="_blank" rel="noopener noreferrer" className="home-play home-play-ext">
                          {game.slug === 'memori' ? 'Play' : 'Wishlist'} <ArrowIcon />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="container home-block">
          <div className="home-sec-head"><span className="label">The deal</span></div>
          <div className="home-deal">
            {DEAL.map((d) => (
              <div className="home-deal-item" key={d.title}>
                <CheckIcon />
                <div><h4>{d.title}</h4><p>{d.desc}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="container home-block">
          <div className="home-sec-head"><span className="label">What we do</span></div>
          <div className="home-wwd">
            {WHAT_WE_DO.map((w) => (
              <div className="home-wwd-card" key={w.k}>
                <span className="k">{w.k}</span>
                <h4>{w.h}</h4>
                <p>{w.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="container home-block">
          <div className="home-cta-band">
            <h2>Have a game? Let's talk.</h2>
            <p>Tell us what you are building. We read every submission, and the first conversation is always free.</p>
            <div className="cta-row home-cta">
              <Link to="/contact" className="primary-btn"><PlayIcon />Launch your game</Link>
              <a href="https://discord.com/invite/U7dYpq5yMK" target="_blank" rel="noopener noreferrer nofollow" className="btn-ghost">Chat on Discord</a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
